import React from "react";

export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/oabeatx5_QDKMi.jpg";

// 12 preset pixel-art avatars (crisp SVG via DiceBear) — distinct neon seeds
const PRESET_SEEDS = [
  "neon-warrior", "cyber-fox", "void-knight", "pulse-runner",
  "glitch-mage", "synth-rider", "magenta-ace", "cyan-blade",
  "shadow-byte", "phoenix-coil", "hyper-witch", "tron-pilot",
];

// Backgrounds per index for variety (CSS gradient layered behind SVG)
const PRESET_BG = [
  "linear-gradient(135deg,#14b8a6,#3a0ca3)",
  "linear-gradient(135deg,#14b8a6,#1e2a3a)",
  "linear-gradient(135deg,#14b8a6,#0080ff)",
  "linear-gradient(135deg,#ffcc00,#14b8a6)",
  "linear-gradient(135deg,#7a18ff,#14b8a6)",
  "linear-gradient(135deg,#ff5500,#14b8a6)",
  "linear-gradient(135deg,#14b8a6,#14b8a6)",
  "linear-gradient(135deg,#1e2a3a,#5500ff)",
  "linear-gradient(135deg,#1a1a2e,#14b8a6)",
  "linear-gradient(135deg,#0080ff,#14b8a6)",
  "linear-gradient(135deg,#14b8a6,#ffcc00)",
  "linear-gradient(135deg,#1e2a3a,#14b8a6)",
];

// ---------------------------------------------------------------------------
// PHOTO_AVATARS — high-quality cyberpunk operative portraits (IDs 12+).
//
// Unlike the 12 DiceBear pixel-art presets above, these are full-resolution
// images chosen by the user as suggested looks for their card. Each entry
// holds a `src` (CDN URL) + a `label` (used as alt text + a small caption
// in the picker so users can pick by vibe, not by index number).
//
// To add new operatives later, append to this array — `avatarSrcFor` will
// pick them up automatically thanks to the index-math below. Keep the
// order stable; existing user records reference the index, not the URL.
// ---------------------------------------------------------------------------
export const PHOTO_AVATARS = [
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/9699ep46_zMwIF.jpg",
    label: "GHOST · cyan-lattice",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/yl2wisvb_zagEA.jpg",
    label: "EMBER · sun-arc",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/vdnxx3z7_XxPwu.jpg",
    label: "VYPER · green-tear",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/hl68j1zn_xSJf2.jpg",
    label: "HEX · circuit-mask",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/vnwkw47p_ViF2B.jpg",
    label: "VETERAN · grey-knight",
  },
  // ---- Drop 2 (Phase 42) — 5 more operatives ----
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/4q2zmj6q_3UUaQ.jpg",
    label: "ORACLE · amber-iris",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/f3qyu9oa_8lASG.jpg",
    label: "PYRE · green-circuit",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/namj6l4l_qR9P7.jpg",
    label: "WRAITH · white-mesh",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/6vmskx1z_nmjj9.jpg",
    label: "AYAKA · cyan-circuit",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/2g44fupi_aXRvg.jpg",
    label: "PHOENIX · gold-rim",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_league-hub-neon/artifacts/uha7684l_m9pal.jpg",
    label: "RAVEN · teal-streak",
  },
];

// First photo-avatar index = right after the 12 DiceBear presets.
const PHOTO_AVATAR_OFFSET = PRESET_SEEDS.length;          // 12
const MAX_AVATAR_ID = PHOTO_AVATAR_OFFSET + PHOTO_AVATARS.length - 1;

// Helper for callers that need to know whether a given id is a portrait
// (so they can skip `imageRendering: pixelated`).
export function isPhotoAvatar(avatarId) {
  return (
    avatarId != null &&
    avatarId >= PHOTO_AVATAR_OFFSET &&
    avatarId <= MAX_AVATAR_ID
  );
}

export function avatarSrcFor(avatarId) {
  if (avatarId == null || avatarId < 0 || avatarId > MAX_AVATAR_ID) return null;
  if (avatarId < PHOTO_AVATAR_OFFSET) {
    const seed = PRESET_SEEDS[avatarId];
    return `https://api.dicebear.com/8.x/pixel-art/svg?seed=${encodeURIComponent(seed)}&radius=12&backgroundType=gradientLinear&backgroundRotation=45`;
  }
  return PHOTO_AVATARS[avatarId - PHOTO_AVATAR_OFFSET].src;
}

export function AvatarChip({ avatarId, pfpUrl, size = 40, selected = false, onClick, dataTestid }) {
  const isCustom = !!pfpUrl;
  const presetSrc = avatarSrcFor(avatarId);
  const isPlaceholder = !isCustom && !presetSrc;
  const photoMode = isCustom || isPhotoAvatar(avatarId);
  // Pixel-art presets keep their layered gradient background; photo
  // operatives use a flat panel so the image edges read cleanly.
  const bg = avatarId != null && !isPhotoAvatar(avatarId)
    ? PRESET_BG[avatarId]
    : "linear-gradient(135deg,#1e2a3a,#14b8a6)";

  return (
    <div
      onClick={onClick}
      data-testid={dataTestid}
      className={`avatar-tile ${selected ? "selected" : ""}`}
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        background: photoMode ? "#11112b" : bg,
        cursor: onClick ? "pointer" : "default",
        border: "2px solid rgba(20, 184, 166,0.45)",
        borderRadius: 16,
        overflow: "hidden",
        position: "relative",
        // Pixelated rendering looks good on the DiceBear SVGs but
        // would shred the photo portraits — toggle by mode.
        imageRendering: photoMode ? "auto" : "pixelated",
      }}
    >
      {isCustom ? (
        <img
          src={pfpUrl}
          alt="avatar"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : presetSrc ? (
        <img
          src={presetSrc}
          alt={`avatar ${avatarId}`}
          style={{
            width: "100%", height: "100%", objectFit: "cover", display: "block",
            imageRendering: photoMode ? "auto" : "pixelated",
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white text-lg font-bold">
          ?
        </div>
      )}
    </div>
  );
}

export default AvatarChip;
